import React from 'react'
import { FormControl, InputBase, Select, withStyles } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import makeStyles from '@material-ui/core/styles/makeStyles'
import styles from '../../styles'
import MenuItem from '@material-ui/core/MenuItem'
import selectColorEnum from '../../enums/selectEnums/SelectColorEnum'
import classNames from 'classnames'
import selectSizeEnum from '../../enums/selectEnums/SelectSizeEnum'
import { FieldRenderProps } from 'react-final-form'

export const CustomSelect = withStyles({
	select: {},
	iconOpen: {
		transform: 'none'
	}
})(Select)

export const CustomInput = withStyles((theme) => ({
	root: {
		height: '100%',
		color: theme.palette.blue.main
	},
	input: {
		'&:focus': {
			backgroundColor: 'transparent',
			borderRadius: 40
		}
	}
}))(InputBase)

const useStyle = makeStyles((theme) => ({
		...styles(theme),
		formControl: {
			minWidth: 120,
			borderRadius: 40,
			'& svg': {
				paddingRight: 24,
				marginLeft: 24,
				width: 24
			}
		},
		smallFormControl: {
			minWidth: 82,
			borderRadius: 24,
			'& svg': {
				paddingRight: 18,
				marginLeft: 10,
				width: 18
			}
		},
		formColorGrey: {
			backgroundColor: theme.palette.blue['5']
		},
		formColorWhite: {
			backgroundColor: 'white'
		},
		dropDown: {
			minWidth: 120,
			borderRadius: 24,
			boxShadow: '0 25px 50px 0 rgba(18, 41, 65, 0.1)'
		},
		smallDropDown: {
			minWidth: 82,
			borderRadius: 15,
			boxShadow: '0 25px 50px 0 rgba(18, 41, 65, 0.1)'
		},
		menuItemRoot: {
			padding: '15px 24px',
			paddingRight: 72,
			'&$menuItemSelected, &$menuItemSelected:focus, &$menuItemSelected:hover': {
				backgroundColor: 'transparent',
				color: theme.palette.blue.main
			}
		},
		smallMenuItemRoot: {
			minHeight: 10,
			padding: '10px 18px',
			paddingRight: 46,
			'&$menuItemSelected, &$menuItemSelected:focus, &$menuItemSelected:hover': {
				backgroundColor: 'transparent',
				color: theme.palette.blue.main
			}
		},
		menuItemSelected: {},
		bigCustomSelect: {
			display: 'flex',
			padding: '15px 0px',
			paddingLeft: 24,
			'&.MuiSelect-select.MuiSelect-select': {
				paddingRight: 72
			}
		},
		smallCustomSelect: {
			display: 'flex',
			padding: '10px 0px',
			paddingLeft: 18,
			'&.MuiSelect-select.MuiSelect-select': {
				paddingRight: 46
			}
		},
		listPadding: {
			padding: 0
		},
		errorStyle: {
			color: '#B92D00',
			fontSize: '10px'
		},
		selectContainer: {
			display: 'flex',
			alignItems: 'center',
			flexDirection: 'column'
		}
	}
))

interface AcceptingProps {
	inputTextStyle?: string,
	backgroundColor?: string,
	size?: string,
	multiple?: boolean,
	errorMessage?: string
}

type FormSelectFieldProps = AcceptingProps & FieldRenderProps<string>

const FormSelectField: React.FC<FormSelectFieldProps> = (
	{
		input,
		inputTextStyle,
		children,
		backgroundColor,
		size,
		errorMessage,
		multiple = false,
		meta: {
			error,
			touched
		}
	}
) => {
	const classes = useStyle()

	const whichColor = (backgroundColor: string | undefined) => {
		switch (backgroundColor) {
			case selectColorEnum.grey :
				return classes.formColorGrey
			case selectColorEnum.white:
				return classes.formColorWhite
			default :
				return classes.formColorWhite
		}
	}

	const whichFormControl = (size: string | undefined) => {
		switch (size) {
			case selectSizeEnum.big :
				return classes.formControl
			case selectSizeEnum.small :
				return classes.smallFormControl
			default :
				return classes.formControl
		}
	}

	const whichMenuItemRoot = (size: string | undefined) => {
		switch (size) {
			case selectSizeEnum.big :
				return classes.menuItemRoot
			case selectSizeEnum.small :
				return classes.smallMenuItemRoot
			default :
				return classes.menuItemRoot
		}
	}

	const whichCustomSelect = (size: string | undefined) => {
		switch (size) {
			case selectSizeEnum.big :
				return classes.bigCustomSelect
			case selectSizeEnum.small :
				return classes.smallCustomSelect
			default :
				return classes.bigCustomSelect
		}
	}

	const whichDropDown = (size: string | undefined) => {
		switch (size) {
			case selectSizeEnum.big :
				return classes.dropDown
			case selectSizeEnum.small :
				return classes.smallDropDown
			default :
				return classes.dropDown
		}
	}

	const formClass = classNames(
		whichFormControl(size),
		whichColor(backgroundColor)
	)

	const customClass = {
		classes: {
			root: whichMenuItemRoot(size),
			selected: classes.menuItemSelected
		},
		ListItemClasses: {
			root: inputTextStyle
		}
	}

	const modifyChildren = (children: React.ReactNode): any =>
		React.Children.map(children, child => {
			if (!React.isValidElement(child)) {
				return child
			}
			let elementChild: React.ReactElement = child
			if (elementChild.type === MenuItem) {
				return React.cloneElement(elementChild, customClass)
			}
			return elementChild
		})

	return (
		<>
			<div className={classes.selectContainer}>
				<FormControl className={formClass}>
					<CustomSelect
						{...input}
						displayEmpty
						input={<CustomInput className={inputTextStyle} />}
						IconComponent={ExpandMoreIcon}
						autoWidth
						MenuProps={{
							classes: {
								paper: whichDropDown(size),
								list: classes.listPadding
							},
							getContentAnchorEl: null,
							anchorOrigin: {
								vertical: 'top',
								horizontal: 'left'
							}
						}}
						classes={{
							select: whichCustomSelect(size)
						}}
					>
						{React.Children.map(children, child => modifyChildren(child))}
					</CustomSelect>
				</FormControl>
			</div>
			{
				touched && (error || errorMessage) && <span className={classes.errorStyle}>{error || errorMessage}</span>
			}
		</>
	)
}

export default FormSelectField
